// Geometría compartida por TierPill y SegmentPill: un solo aspecto —relleno pastel y borde de 1px—
// en dos densidades: `xs` (9.5px), la norma del backoffice, y `sm` (11px), la del wizard.
import { cn } from '@/lib/cn';

export type PillSize = 'sm' | 'xs';

const PILL_BASE =
    'inline-flex max-w-full items-center whitespace-nowrap rounded-full border font-semibold leading-none';

const PILL_SIZE_CLASS: Record<PillSize, string> = {
    sm: 'gap-[5px] px-2 py-1 text-[11px]',
    xs: 'gap-1 px-1.5 py-0.5 text-[9.5px]',
};

const PILL_DOT_SIZE_CLASS: Record<PillSize, string> = {
    sm: 'size-[7px]',
    xs: 'size-[6px]',
};

/** Solo se puede prender lo que se puede apagar: el modo interactivo agrega transición y el cursor. */
const PILL_INTERACTIVE_CLASS =
    'cursor-pointer transition-colors aria-disabled:cursor-not-allowed aria-disabled:opacity-60';

/**
 * El apagado: gris neutro IGUAL para todos, nunca el color del dato. Con su color, prendido y
 * apagado se confunden y el filtro deja de leerse.
 */
export const PILL_OFF_CLASS = 'border-tier-none-soft-border bg-card text-tier-none';

export const PILL_OFF_DOT_CLASS = 'bg-tier-none/60';

export interface PillClassOptions {
    size: PillSize;
    /** Relleno, texto y borde del dato. Se ignora cuando la píldora está apagada. */
    tone: string;
    off: boolean;
    interactive: boolean;
    className?: string;
}

/** Las clases del chip: chasis, densidad y —según el estado— el color del dato o el gris apagado. */
export function pillClass({ size, tone, off, interactive, className }: PillClassOptions): string {
    return cn(
        PILL_BASE,
        PILL_SIZE_CLASS[size],
        off ? PILL_OFF_CLASS : tone,
        interactive && PILL_INTERACTIVE_CLASS,
        className,
    );
}

/** Las clases del punto de color, del tamaño que le toca a su densidad. */
export function pillDotClass(size: PillSize, tone: string, off: boolean): string {
    return cn(
        'flex-none rounded-full',
        PILL_DOT_SIZE_CLASS[size],
        off ? PILL_OFF_DOT_CLASS : tone,
    );
}
